// Admin → API Status. Two things, from two endpoints:
//
//   - every external API CXHub talks to and whether read / write (or inference)
//     is enabled, from the server-side integrations registry
//     (server/src/integrations/status.js) — adding a connector there makes it
//     appear here automatically
//   - per-mailbox EMAIL INGEST health (server/src/channels/m365/health.js),
//     which answers the question the flag list cannot: did mail arrive and fail
//     to get in
//
// The second is a live read over Graph rather than a flag, so it is fetched
// separately and rendered independently — see load() below.

import { useEffect, useState } from 'react'
import {
  Box, Typography, Stack, Chip, Paper, Divider, CircularProgress, Button, Alert, Link,
} from '@mui/material'
import { fetchIntegrations, fetchM365Health, startEbayConsent } from './api.js'

// One mailbox's ingest state → how it should read at a glance.
//
// `paused` is deliberately quiet. An account switched off in Run-Time Flags is
// SUPPOSED to be accumulating mail, and colouring that as a fault is how a
// health panel becomes something people scroll past — at which point the one
// row that matters goes unread too.
const HEALTH_STATES = {
  ok: { color: 'success', label: 'ok' },
  paused: { color: 'default', label: 'paused' },
  backed_up: { color: 'error', label: 'backed up' },
  needs_human: { color: 'warning', label: 'needs a human' },
  stale: { color: 'warning', label: 'stale' },
  error: { color: 'error', label: 'error' },
  unreachable: { color: 'error', label: 'unreachable' },
}

function MailboxHealth({ mailboxes, failed }) {
  // "Could not check" is not "nothing wrong" — say so rather than render an
  // empty, reassuring panel.
  if (failed) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Could not read mailbox ingest health. This says nothing about whether ingest is working.
      </Alert>
    )
  }
  if (!mailboxes?.length) return null

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" color="primary">Email ingest</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: '80ch' }}>
        Mail sitting in a mailbox&apos;s source folder arrived but did not ingest. In steady state that
        folder is empty, because ingesting a message moves it to CXHub/Processed.
      </Typography>
      <Stack spacing={1}>
        {mailboxes.map((m) => {
          const s = HEALTH_STATES[m.state] ?? { color: 'default', label: m.state }
          return (
            <Paper key={m.account} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{m.account}</Typography>
                <Typography variant="caption" color="text.secondary">{m.mailbox}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip size="small" color={s.color} label={s.label} />
              </Box>
              {m.detail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {m.detail}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                {m.sourceFolder} {m.sourceCount ?? '—'} · Processed {m.processedCount ?? '—'} · Failed {m.failedCount ?? '—'}
                {m.lastSyncedAt ? ` · last sync ${new Date(m.lastSyncedAt).toLocaleString()}` : ' · never synced'}
              </Typography>
            </Paper>
          )
        })}
      </Stack>
    </Box>
  )
}

// A credential nearing / past its expiry → a banner. eBay refresh tokens expire
// (~18 months) and silently take the channel down; this is the in-app warning
// (a scheduled workflow also opens a GitHub issue). Only warning/expired tokens
// surface here — healthy ones stay quiet.
function TokenExpiryBanner({ tokenHealth }) {
  const alerts = (tokenHealth ?? []).filter((t) => t.state !== 'ok')
  if (alerts.length === 0) return null
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {alerts.map((t) => {
        const date = new Date(t.expiresOn).toLocaleDateString()
        const expired = t.state === 'expired'
        const RUNBOOK = 'https://github.com/1AAuto/CXHubV2/blob/main/docs/ebay-oauth.md#renewal-runbook-every-18-months-or-on-demand'
        return (
          <Alert key={t.key} severity={expired ? 'error' : 'warning'}>
            {expired
              ? `The ${t.name} expired on ${date}. eBay ingestion is down until it's renewed.`
              : `The ${t.name} expires in ${t.daysRemaining} day${t.daysRemaining === 1 ? '' : 's'} (${date}). Renew it before then.`}
            {' '}Renew it with the eBay seller consent button below, in a browser signed into that
            eBay seller account, then update the repo variable — steps in the{' '}
            <Link href={RUNBOOK} target="_blank" rel="noopener">renewal runbook</Link>.
            {' '}Do <b>not</b> use eBay's "Get a User Token" tool — it returns an access token, not a refresh token.
          </Alert>
        )
      })}
    </Stack>
  )
}

// Starts an eBay seller consent for one account. The consent URL carries a
// one-time state that the server issues only to a signed-in admin, so it cannot
// be a plain link: fetch it, then open it. The tab is opened BEFORE the fetch so
// the browser treats it as part of the click, not as a pop-up.
function EbayConsent({ companies }) {
  const [error, setError] = useState(null)
  if (companies.length === 0) return null
  const start = async (company) => {
    setError(null)
    const tab = window.open('', '_blank')
    try {
      const { url } = await startEbayConsent(company)
      if (tab) {
        tab.opener = null
        tab.location.href = url
      } else {
        window.location.href = url
      }
    } catch (err) {
      if (tab) tab.close()
      setError(`Could not start the consent for ${company}: ${err.message}`)
    }
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>eBay seller consent</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, maxWidth: '80ch' }}>
        Replaces the account&apos;s stored refresh token with a new one. Sign in to eBay as that
        seller account in the tab that opens. The link is valid for 10 minutes.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        {companies.map((c) => (
          <Button key={c} size="small" variant="outlined" onClick={() => start(c)}>
            Renew consent: {c}
          </Button>
        ))}
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Paper>
  )
}

// One capability's state → a chip.
function StateChip({ state }) {
  if (state === 'enabled') return <Chip size="small" color="success" label="enabled" />
  if (state === 'disabled') return <Chip size="small" color="default" variant="outlined" label="disabled" />
  return <Chip size="small" variant="outlined" label="n/a" sx={{ color: 'text.disabled', borderStyle: 'dashed' }} />
}

function IntegrationCard({ it }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{it.name}</Typography>
        <Chip size="small" label={it.category} variant="outlined" sx={{ height: 18, fontSize: 10.5 }} />
      </Box>
      {it.detail && <Typography variant="caption" color="text.secondary">{it.detail}</Typography>}
      <Divider sx={{ my: 0.5 }} />
      <Stack spacing={0.75}>
        {it.capabilities.map((c) => (
          <Box key={c.kind} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 78, color: 'text.secondary' }}>{c.kind}</Typography>
            <StateChip state={c.state} />
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

export default function ApiStatusView() {
  const [integrations, setIntegrations] = useState(null)
  const [tokenHealth, setTokenHealth] = useState([])
  const [ebayConsentCompanies, setEbayConsentCompanies] = useState([])
  const [mailboxes, setMailboxes] = useState([])
  const [mailboxesFailed, setMailboxesFailed] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    fetchIntegrations().then(({ integrations, tokenHealth, ebayConsentCompanies }) => {
      setIntegrations(integrations)
      setTokenHealth(tokenHealth)
      setEbayConsentCompanies(ebayConsentCompanies)
      setLoading(false)
    })
    // Deliberately NOT awaited alongside the above: this one makes Graph calls
    // per mailbox, and the flag-derived list should not wait on a slow or
    // unreachable mailbox to render.
    fetchM365Health().then(({ mailboxes, failed }) => {
      setMailboxes(mailboxes)
      setMailboxesFailed(failed)
    })
  }
  useEffect(load, [])

  // Group by category for a tidy layout.
  const groups = (integrations ?? []).reduce((acc, it) => {
    (acc[it.category] ??= []).push(it)
    return acc
  }, {})

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6">API Status</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '80ch' }}>
        Every external API CXHub connects to, with its live read / write state from the feature flags.
        New connectors appear here automatically as they&apos;re wired up.
      </Typography>

      <TokenExpiryBanner tokenHealth={tokenHealth} />

      <EbayConsent companies={ebayConsentCompanies} />

      <MailboxHealth mailboxes={mailboxes} failed={mailboxesFailed} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : integrations.length === 0 ? (
        <Typography color="text.secondary">No integrations reported.</Typography>
      ) : (
        <Stack spacing={3}>
          {Object.entries(groups).map(([category, items]) => (
            <Box key={category}>
              <Typography variant="overline" color="primary">{category}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, mt: 0.5 }}>
                {items.map((it) => <IntegrationCard key={it.key} it={it} />)}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
